import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import '../components/AdminLayout.css';

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterUniversity, setFilterUniversity] = useState('');
  const [filterFaculty, setFilterFaculty] = useState('');
  const [universities, setUniversities] = useState([]);
  const [faculties, setFaculties] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [banModal, setBanModal] = useState(null);
  const [banReason, setBanReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    let query = supabase
      .from('profiles')
      .select('id, name, email, university, faculty, created_at, updated_at, role, is_banned, ban_reason, banned_at')
      .order('created_at', { ascending: false });

    if (filterUniversity) query = query.eq('university', filterUniversity);
    if (filterFaculty) query = query.eq('faculty', filterFaculty);

    const { data, error } = await query;
    if (!error && data) {
      const filtered = search
        ? data.filter(u =>
            (u.name || '').toLowerCase().includes(search.toLowerCase()) ||
            (u.email || '').toLowerCase().includes(search.toLowerCase()))
        : data;
      setUsers(filtered);

      const unis = [...new Set(data.map(u => u.university).filter(Boolean))];
      const facs = [...new Set(data.map(u => u.faculty).filter(Boolean))];
      setUniversities(unis);
      setFaculties(facs);
    }
    setLoading(false);
  }, [search, filterUniversity, filterFaculty]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  async function handleBan() {
    if (!banModal) return;
    setActionLoading(true);
    const { error } = await supabase.from('profiles').update({
      is_banned: true,
      ban_reason: banReason,
      banned_at: new Date().toISOString(),
    }).eq('id', banModal.id);
    if (error) console.error('Ban error:', error);
    setActionLoading(false);
    if (!error) {
      setBanModal(null);
      setBanReason('');
      fetchUsers();
      if (selectedUser?.id === banModal.id) setSelectedUser(prev => ({ ...prev, is_banned: true, ban_reason: banReason }));
    }
  }

  async function handleUnban(user) {
    setActionLoading(true);
    const { error } = await supabase.from('profiles').update({
      is_banned: false,
      ban_reason: null,
      banned_at: null,
    }).eq('id', user.id);
    setActionLoading(false);
    if (!error) {
      fetchUsers();
      if (selectedUser?.id === user.id) setSelectedUser(prev => ({ ...prev, is_banned: false }));
    }
  }

  function fmtDate(d) {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('tr-TR');
  }

  return (
    <div>
      {/* Filters */}
      <div className="admin-filter-bar">
        <input
          className="admin-search-input"
          placeholder="İsim veya e-posta ile ara..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select className="admin-select" value={filterUniversity} onChange={e => setFilterUniversity(e.target.value)}>
          <option value="">Tüm Üniversiteler</option>
          {universities.map(u => <option key={u} value={u}>{u}</option>)}
        </select>
        <select className="admin-select" value={filterFaculty} onChange={e => setFilterFaculty(e.target.value)}>
          <option value="">Tüm Fakülteler</option>
          {faculties.map(f => <option key={f} value={f}>{f}</option>)}
        </select>
        <button className="btn-admin btn-admin-primary" onClick={fetchUsers}>Filtrele</button>
      </div>

      {/* Table */}
      <div className="admin-table-wrapper">
        <div className="admin-table-scroll">
          <table className="admin-table">
            <thead>
              <tr>
                <th>İsim</th>
                <th>E-posta</th>
                <th>Üniversite</th>
                <th>Fakülte</th>
                <th>Kayıt Tarihi</th>
                <th>Durum</th>
                <th>İşlem</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr className="admin-loading-row"><td colSpan={7}>Yükleniyor...</td></tr>
              ) : users.length === 0 ? (
                <tr className="admin-loading-row"><td colSpan={7}>Kullanıcı bulunamadı.</td></tr>
              ) : users.map(u => (
                <tr key={u.id}>
                  <td>
                    <button
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#5784E6', fontWeight: 600, padding: 0, fontSize: '0.875rem', fontFamily: 'inherit' }}
                      onClick={() => setSelectedUser(u)}
                    >
                      {u.name || '—'}
                    </button>
                  </td>
                  <td>{u.email || '-'}</td>
                  <td>{u.university || '-'}</td>
                  <td>{u.faculty || '-'}</td>
                  <td>{fmtDate(u.created_at)}</td>
                  <td>
                    {u.is_banned
                      ? <span className="badge badge-red">Yasaklı</span>
                      : <span className="badge badge-green">Aktif</span>}
                  </td>
                  <td>
                    {u.is_banned ? (
                      <button className="btn-admin btn-admin-success" onClick={() => handleUnban(u)} disabled={actionLoading}>
                        Yasağı Kaldır
                      </button>
                    ) : (
                      <button className="btn-admin btn-admin-danger" onClick={() => { setBanModal(u); setBanReason(''); }}>
                        Yasakla
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="admin-table-footer">Toplam {users.length} kullanıcı gösteriliyor</div>
      </div>

      {/* User Detail Drawer */}
      {selectedUser && (
        <div className="admin-drawer-overlay" onClick={() => setSelectedUser(null)}>
          <div className="admin-drawer" onClick={e => e.stopPropagation()}>
            <div className="admin-drawer-header">
              <h3>Kullanıcı Detayı</h3>
              <button className="admin-drawer-close" onClick={() => setSelectedUser(null)}>✕</button>
            </div>
            <div className="admin-drawer-body">
              <div className="detail-row"><label>Ad Soyad</label><span>{selectedUser.name || '-'}</span></div>
              <div className="detail-row"><label>E-posta</label><span>{selectedUser.email || '-'}</span></div>
              <div className="detail-row"><label>Üniversite</label><span>{selectedUser.university || '-'}</span></div>
              <div className="detail-row"><label>Fakülte</label><span>{selectedUser.faculty || '-'}</span></div>
              <div className="detail-row"><label>Kayıt Tarihi</label><span>{fmtDate(selectedUser.created_at)}</span></div>
              <div className="detail-row"><label>Güncelleme Tarihi</label><span>{fmtDate(selectedUser.updated_at)}</span></div>
              <div className="detail-row">
                <label>Durum</label>
                <span>
                  {selectedUser.is_banned
                    ? <span className="badge badge-red">Yasaklı</span>
                    : <span className="badge badge-green">Aktif</span>}
                </span>
              </div>
              {selectedUser.is_banned && selectedUser.ban_reason && (
                <div className="detail-row"><label>Yasak Nedeni</label><span>{selectedUser.ban_reason}</span></div>
              )}
              {selectedUser.is_banned && selectedUser.banned_at && (
                <div className="detail-row"><label>Yasaklanma Tarihi</label><span>{fmtDate(selectedUser.banned_at)}</span></div>
              )}
              <hr className="detail-divider" />
              <div style={{ display: 'flex', gap: 8 }}>
                {selectedUser.is_banned ? (
                  <button className="btn-admin btn-admin-success" onClick={() => handleUnban(selectedUser)} disabled={actionLoading}>
                    Yasağı Kaldır
                  </button>
                ) : (
                  <button className="btn-admin btn-admin-danger" onClick={() => { setBanModal(selectedUser); setBanReason(''); }}>
                    Kullanıcıyı Yasakla
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ban Modal */}
      {banModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal">
            <h3>Kullanıcıyı Yasakla</h3>
            <p><strong>{banModal.name}</strong> adlı kullanıcı yasaklanacak. Bu işlem geri alınabilir.</p>
            <div className="afield" style={{ marginBottom: 20 }}>
              <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#3D4F7C', marginBottom: 6, display: 'block' }}>Yasak Nedeni</label>
              <textarea
                style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #E0E6F7', borderRadius: 10, fontSize: '0.875rem', fontFamily: 'inherit', resize: 'vertical', minHeight: 80, outline: 'none' }}
                placeholder="Yasak nedenini belirtin..."
                value={banReason}
                onChange={e => setBanReason(e.target.value)}
              />
            </div>
            <div className="admin-modal-actions">
              <button className="btn-admin btn-admin-outline" onClick={() => setBanModal(null)}>İptal</button>
              <button className="btn-admin btn-admin-danger" onClick={handleBan} disabled={actionLoading || !banReason.trim()}>
                {actionLoading ? 'İşleniyor...' : 'Yasakla'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
